import { mountVue } from '../vue/mountVue';
import Search from '../vue/Search/SearchInput.vue';
import { releaseFocus } from '../utils/global-functions';

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement|null} Element to return focus to when the dialog closes. */
    this.triggerElement = null;
    this.clickSearchIcon();
    this.clickTryAgain();
  }

  /**
   * Wire the header search buttons to open the dialog. Listening for the
   * `click` event (which fires on mouse click and on keyboard Enter/Space for
   * a native button) keeps the control fully keyboard operable.
   */
  clickSearchIcon() {
    document.querySelectorAll('.header__search').forEach((searchIcon) => {
      searchIcon.addEventListener('click', () => this.initSearch(searchIcon));
    });
  }

  /**
   * Re-open the dialog from the "try again" button on the empty search results
   * template.
   */
  clickTryAgain() {
    const tryAgainButton = document.querySelector('.template-search--empty')
      ? document.querySelector('.results__button')
      : null;
    if (tryAgainButton) {
      tryAgainButton.addEventListener('click', () =>
        this.initSearch(tryAgainButton)
      );
    }
  }

  /**
   * Open the dialog, remembering the control that triggered it so focus can be
   * returned there on close.
   * @param {HTMLElement} [trigger] - The element that opened the dialog.
   */
  initSearch(trigger = document.activeElement) {
    this.triggerElement = trigger;
    const rect = document
      .querySelector('sticky-header, static-nav')
      .getBoundingClientRect();
    this.style.top = `${rect.top}px`;
    this.toggle();
    if (this.querySelector('.js-main-search'))
      mountVue('.js-main-search', Search, this);
  }

  /**
   * Open or close the dialog depending on its current visibility.
   */
  toggle() {
    if (this.classList.contains('hidden')) this.open();
    else this.close();
  }

  /**
   * Reveal the dialog, expose it to assistive technology, hide the obscured
   * background, and move focus to the search field.
   */
  open() {
    this.classList.remove('hidden');
    this.removeAttribute('aria-hidden');
    document
      .querySelector('.predictive-search--overlay')
      .setAttribute('open', '');
    document.documentElement.classList.add('overflow-hidden');

    const input = this.querySelector('.search__bar--input');
    if (input) input.focus();

    this.setBackgroundHidden(true);
  }

  /**
   * Hide the dialog, restore the background for assistive technology, and
   * return focus to the control that opened it.
   */
  close() {
    this.setBackgroundHidden(false);
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
    document
      .querySelector('.predictive-search--overlay')
      .removeAttribute('open');
    document.documentElement.classList.remove('overflow-hidden');
    releaseFocus(this.triggerElement);
  }

  /**
   * Toggle `aria-hidden` on every page region outside the dialog so screen
   * reader users cannot reach the obscured background content while the dialog
   * is open. The dialog lives in the navigation section as a sibling of the
   * header wrapper, so both the other body-level regions and that wrapper are
   * hidden. Keyboard containment is handled separately by the focus trap.
   * @param {boolean} isHidden - Whether the background should be hidden.
   */
  setBackgroundHidden(isHidden) {
    const regions = Array.from(document.body.children).filter(
      (child) => !child.contains(this)
    );

    const section = this.closest('.shopify-section');
    if (section) {
      section.querySelectorAll(':scope > *').forEach((child) => {
        if (child !== this) regions.push(child);
      });
    }

    regions.forEach((region) => {
      if (isHidden) region.setAttribute('aria-hidden', 'true');
      else region.removeAttribute('aria-hidden');
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
